import { Providers } from "@microsoft/mgt-element";
import axios, { Method } from "axios";
import { IAgenciesData, IDivisionData, IDocumentTypeData } from "../models/IFiltersData";
import { IDataService } from "./IDataService";
import { appInsights } from "../AppInsights";
import { IDocumentHistory, IRelatedDocumentsIndex, ISearch, ISearchHistory, ISearchResult, IUpdateDocument } from "../intefaces";

export class DataService implements IDataService {

    public CallSearchApi(searchText: string, searchSize: number, searchSkip: number, sortingDesc: boolean, relevanceOrder:boolean = false): Promise<ISearchResult[]> {
        return this.callSearchApi(searchText, searchSize, searchSkip, sortingDesc, relevanceOrder);
    }

    public CallAgenciesApi(): Promise<IAgenciesData[]> {
        return this.callAgenciesApi();
    }

    public CallDivisionApi(): Promise<IDivisionData[]> {
        return this.callDivisionApi();
    }

    public CallDocumentTypeApi(): Promise<IDocumentTypeData[]> {
        return this.callDocumentTypeApi();
    }

    public CallGetDocument(id: string): Promise<ISearch[]> {
        return this.callGetDocument(id);
    }

    public CallRelatedDocumentsApi(search: string[]): Promise<IRelatedDocumentsIndex[]> {
        return this.callRelatedDocumentsApi(search);
    }

    public CallUpdateDocumentApi(index: IUpdateDocument): Promise<any> {
        return this.callUpdateDocumentApi(index);
    }

    public CallDeleteDocumentApi(id: string): Promise<any> {
        return this.callDeleteDocumentApi(id);
    }

    public CallCheckIfBlobExists(url: string): Promise<any> {
        return this.callCheckIfBlobExists(url);
    }

    public CallUpdateSearchHistory(searchHistory: ISearchHistory): Promise<any> {
        return this.callUpdateSearchHistory(searchHistory);
    }

    public CallGetSearchHistory(): Promise<ISearchHistory[]> {
        return this.callGetSearchHistory();
    }

    public CallUpdateDocumentHistory(documentHistory: IDocumentHistory): Promise<any> {
        return this.callUpdateDocumentHistory(documentHistory);
    }

    public CallGetDocumentHistory(): Promise<IDocumentHistory[]> {
        return this.callGetDocumentHistory();
    }

    private async getToken() {
        return await Providers.globalProvider.getAccessToken({ scopes: ['user.read', 'openid', 'profile', 'Directory.AccessAsUser.All'] });
    }

    private async getGroups() {
        var groupsArray: string[] = [];
        var groupsIteratedThrough = false;
        var groupQueryUrl = `/me/transitiveMemberOf/microsoft.graph.group?$count=true&$top=999&$search="displayName:${process.env.REACT_APP_DIRECTORY_PREFIX}"&$select=displayName`;
   
        while(!groupsIteratedThrough){
            var groups = await Providers.globalProvider.graph
                .api(groupQueryUrl)
                .header('Content-Type', 'application/json')
                .header('ConsistencyLevel', 'eventual')
                .select('displayName')
                .get();
            
            groups.value.map((c: any) => {
                groupsArray.push(c.displayName);
            });
            if(!groups["@odata.nextLink"]){
                groupsIteratedThrough = true
            }
            else{
                groupQueryUrl = groups["@odata.nextLink"].substring(groups["@odata.nextLink"].indexOf("/me"));
            }
        }
        
        localStorage.setItem('securityGroup', JSON.stringify(groupsArray));
        return groupsArray;
    }

    private async APICall(params: string, url: string, method: Method, errMessage: string) {
        let result: any;

        axios.defaults.baseURL = process.env.REACT_APP_SEARCHAPI;
        axios.defaults.headers = { 'Access-Control-Allow-Origin': '*' };

        await axios({
            method: method,
            url: url,
            data: params,
            headers: {
                "Content-Type": "multipart/form-data",
                "Authorization": this.getToken(),
                "x-functions-key": process.env.REACT_APP_FUNCTIONID
            },
        })
            .then(response => {
                result = response.data;
            }).catch(error => {
                let err = new Error(errMessage + error);
                appInsights.trackException({
                    exception: err,
                    severityLevel: 3
                });
                console.error(err);
            });

        return result;
    }

    private async callSearchApi(searchText: string, searchSize: number, searchSkip: number, sortingDesc: boolean, relevanceOrder:boolean = false): Promise<ISearchResult[]> {
        let agency: string = JSON.parse(localStorage.getItem('agency') || "");
        const params = JSON.stringify({
            'Author': JSON.parse(localStorage.getItem('author') || 'null') ,
            'Title': JSON.parse(localStorage.getItem('title') || 'null'),
            'StartDate': JSON.parse(localStorage.getItem('startDate') || 'null'),
            'EndDate': JSON.parse(localStorage.getItem('endDate') || 'null'),
            'OrderByDesc': sortingDesc,
            'RelevanceOrder': relevanceOrder,
            'Division': JSON.parse(localStorage.getItem('division') || ''),
            'DocumentType': JSON.parse(localStorage.getItem('documentType') || ''),
            'Agency': agency == 'non' ? [] : agency,
            'Directory': localStorage.getItem('securityGroup') || '' == '' ? await this.getGroups() : localStorage.getItem('securityGroup')
        });
        
        appInsights.trackEvent({ name: 'search params', properties: { "parms": params } });
        const fuzzy = localStorage.getItem('fuzzySearch') || 'true';
        let results: ISearchResult[] = [];
        results = await this.APICall(params, `/api/search/${searchText}&size=${searchSize}&skip=${searchSkip}&fuzzy=${fuzzy}`, 'post', '(axios) Get search results:  ');
        return results;
    }


    private async callGetDocument(id: string): Promise<ISearch[]> {
        const params = JSON.stringify({
            'Directory': localStorage.getItem('securityGroup') || '' == '' ? await this.getGroups() : localStorage.getItem('securityGroup')
        });
        appInsights.trackEvent({ name: 'docs params', properties: { "parms": params } });
        let result: ISearch[] = [];
        result = await this.APICall(params, `/api/document/id=${id}`, 'post', '(axios) Get document:  ');
        return result;
    }

    private async callAgenciesApi(): Promise<IAgenciesData[]> {
        let results: IAgenciesData[] = [];
        results = await this.APICall('', `/api/GetAgencies`, 'get', '(axios) Get agencies:  ');
        return results;
    }

    private async callDivisionApi(): Promise<IDivisionData[]> {
        let results: IDivisionData[] = [];
        results = await this.APICall('', `/api/GetDivisions`, 'get', '(axios) Get division:  ');
        return results;
    }

    private async callDocumentTypeApi(): Promise<IDocumentTypeData[]> {
        let results: IDocumentTypeData[] = [];
        results = await this.APICall('', `/api/GetDocumentType`, 'get', '(axios) Get document type:  ');
        return results;
    }

    private async callCheckIfBlobExists(url: string): Promise<any> {
        let result: any  = await this.APICall(url, `/api/CheckIfBlobExists`, 'post', '(axios) Check if blob exists:  ');
        return result;
    }

    private async callRelatedDocumentsApi(search: string[]): Promise<IRelatedDocumentsIndex[]> {
        const params = JSON.stringify({
            'Directory': await this.getGroups(),
            'RelatedKey': search
        });

        let result: IRelatedDocumentsIndex[] = [];
        result = await this.APICall(params, `/api/RelatedDocuments`, 'post', '(axios) Get document:  ');
        return result;
    }

    private async callUpdateDocumentApi(index: IUpdateDocument): Promise<any> {
        const params = JSON.stringify({
            'Directory': await this.getGroups(),
            'id': index.id,
            'Author': index.author,
            'Title': index.title,
            'Division': index.division,
            'ContractDate': index.contractDate,
            'Agency': index.agency,
            'DocumentType': index.documentType,
            'Counterparties': index.counterparties,
            'Counterparties2': index.counterparties2
        });

        let result: any = await this.APICall(params, `/api/UpdateDocument`, 'post', '(axios) Update document:  ');

        return result;
    }

    private async callDeleteDocumentApi(id: string): Promise<any> {
        const params = JSON.stringify({
            'Directory': await this.getGroups(),
            'id': id
        });

        let result: any = await this.APICall(params, `/api/DeleteDocument`, 'post', '(axios) Delete document:  ');
        return result;
    }

    private async callUpdateSearchHistory(searchHistory: ISearchHistory): Promise<any> {
        let userUpn = await Providers.globalProvider.graph.api(`/me`).select('userPrincipalName').get();
        const params = JSON.stringify({
            "userUpn": userUpn['userPrincipalName'],
            "searchTerm": searchHistory.searchTerm,
            "searchUrl": searchHistory.searchUrl
        });

        let result: any = await this.APICall(params, `/api/UpdateSearchHistory`, 'post', '(axios) Update search history: ');
        return result;
    }

    private async callGetSearchHistory(): Promise<ISearchHistory[]> {
        let userUpn = await Providers.globalProvider.graph.api(`/me`).select('userPrincipalName').get();
        const params = JSON.stringify({
            "userUpn": userUpn['userPrincipalName']
        });

        let result: ISearchHistory[] = [];
        result = await this.APICall(params, `/api/GetSearchHistory`, 'post', '(axios) Get search history: ');
        return result;
    }

    private async callUpdateDocumentHistory(documentHistory: IDocumentHistory): Promise<any> {
        let userUpn = await Providers.globalProvider.graph.api(`/me`).select('userPrincipalName').get();
        const params = JSON.stringify({
            "userUpn": userUpn['userPrincipalName'],
            "documentTitle": documentHistory.documentTitle,
            "documentUrl": documentHistory.documentUrl
        });

        let result: any = await this.APICall(params, `/api/UpdateDocumentHistory`, 'post', '(axios) Update document history: ');
        return result;
    }

    private async callGetDocumentHistory(): Promise<IDocumentHistory[]> {
        let result: IDocumentHistory[] = [];
        let userUpn = await Providers.globalProvider.graph.api(`/me`).select('userPrincipalName').get();

        const params = JSON.stringify({
            "userUpn": userUpn['userPrincipalName']
        });

        result = await this.APICall(params, `/api/GetDocumentHistory`, 'post', '(axios) Get document history: ');
        return result;
    }
}